import { useEffect, useRef } from 'react';
import * as THREE from 'three';
import { useDispatch, useSelector } from 'react-redux';
import {
    setCommandMessage,
    resetOperation,
    setOperationData,
    setOperationStep,
} from '../../../redux/operationSlice';
import { generateUniqueId } from '../../../utils/generateUniqueId';
import { COMMANDS } from '../../../utils/commonProperties';
import { createDrawingClickHandler, applyGridSnap, getOrthoSnappedPoint } from "./useDrawingEvents";
import { getSnappedPoint } from '../../../shared/snap/getSnappedPoint';
import { createSnapMarker } from '../../../shared/snap/createSnapMarker';
import { createEscapeHandler } from '../../../utils/escapeHandler';
import { DrawCommand } from '../../../modules/undoRedo';
import { addUserEntity } from '../../../redux/dxfSlice';

const useDrawEllipse = (scene, camera, renderer, snapPoints) => {
    const snapMarkerRef = useRef(null);
    const tempEllipseRef = useRef(null);
    const dispatch = useDispatch();

    const { commandType, step, data, drawMethod } = useSelector(state => state.operation);
    const gridMode = useSelector(state => state.mods.gridMode);
    const objectSnap = useSelector(state => state.mods.objectSnap);
    const orthoMode = useSelector(state => state.mods.orthoMode);
    const snapMode = useSelector((state) => state.mods.snapMode);
    const { activeLayerName } = useSelector((state) => state.layer);
    const activeTabId = useSelector(state => state.projectTabs.activeTabId);

    const serializeVector = (v) => ({ x: v.x, y: v.y, z: v.z });

    useEffect(() => {
        if (commandType !== COMMANDS.DRAW_ELLIPSE || !scene || !camera || !renderer) return;

        const domElement = renderer.domElement;
        const method = drawMethod || 'center_axis';

        const createEllipsePoints = (center, majorRadius, minorRadius, angle) => {
            const points = [];
            for (let i = 0; i <= 64; i++) {
                const theta = (i / 64) * Math.PI * 2;
                const x = majorRadius * Math.cos(theta);
                const y = minorRadius * Math.sin(theta);
                const rotatedX = x * Math.cos(angle) - y * Math.sin(angle);
                const rotatedY = x * Math.sin(angle) + y * Math.cos(angle);
                points.push(new THREE.Vector3(center.x + rotatedX, center.y + rotatedY, 0));
            }
            return points;
        };

        const removeTempEllipse = () => {
            if (tempEllipseRef.current) {
                scene.remove(tempEllipseRef.current);
                tempEllipseRef.current.geometry.dispose();
                tempEllipseRef.current.material.dispose();
                tempEllipseRef.current = null;
            }
        };

        // Initial message
        if (method === 'center_axis') {
            dispatch(setCommandMessage("Merkez noktasını seçin"));
        } else if (method === 'axis_endpoints') {
            dispatch(setCommandMessage("Eksen 1. ucunu seçin"));
        }

        const handleEllipsePoint = (point) => {
            if (method === 'center_axis') {
                if (step === 0) {
                    dispatch(setOperationData({ center: serializeVector(point) }));
                    dispatch(setOperationStep(1));
                    dispatch(setCommandMessage("Uzun kenar (major axis) için tıklayın"));
                } else if (step === 1) {
                    const center = new THREE.Vector3(data.center.x, data.center.y, data.center.z);
                    const major = orthoMode ? getOrthoSnappedPoint(center, point) : point;
                    dispatch(setOperationData({ ...data, major: serializeVector(major) }));
                    dispatch(setOperationStep(2));
                    dispatch(setCommandMessage("Kısa kenar (minor axis) için tıklayın"));
                } else if (step === 2) {
                    const center = new THREE.Vector3(data.center.x, data.center.y, data.center.z);
                    const major = new THREE.Vector3(data.major.x, data.major.y, data.major.z);
                    const majorVec = major.clone().sub(center);
                    const majorRadius = majorVec.length();
                    const minorRadius = point.clone().sub(center).length();
                    const angle = Math.atan2(majorVec.y, majorVec.x);

                    const ellipsePoints = createEllipsePoints(center, majorRadius, minorRadius, angle);
                    const geometry = new THREE.BufferGeometry().setFromPoints(ellipsePoints);
                    const material = new THREE.LineBasicMaterial({ color: 0xffffff });
                    const ellipse = new THREE.LineLoop(geometry, material);
                    ellipse.userData = {
                        id: generateUniqueId(),
                        isSelectable: true,
                        type: 'ellipse',
                        center: center,
                        radiusX: majorRadius,
                        radiusY: minorRadius,
                        rotation: angle,
                        major: major,
                    };
                    scene.add(ellipse);

                    // Redux'a DXF entity olarak kaydet (JBZ'ye dahil edilecek)
                    dispatch(addUserEntity({
                        type: 'ELLIPSE',
                        center: { x: center.x, y: center.y, z: center.z || 0 },
                        majorEndPoint: { x: majorVec.x, y: majorVec.y, z: 0 },
                        axisRatio: minorRadius / majorRadius,
                        startAngle: 0,
                        endAngle: Math.PI * 2,
                        layer: activeLayerName || '0',
                        _userEntityId: ellipse.userData.id,
                        _userDrawn: true,
                        _tabId: activeTabId || 'mimari',
                    }));

                    // Undo-redo kaydı
                    const manager = window.undoRedoManager;
                    if (manager) {
                        const tx = manager.beginTransaction('Draw Ellipse');
                        manager.addCommand(new DrawCommand(ellipse));
                        manager.commitTransaction(tx);
                    }

                    removeTempEllipse();
                    dispatch(resetOperation());
                    dispatch(setCommandMessage(""));
                }
            } else if (method === 'axis_endpoints') {
                if (step === 0) {
                    dispatch(setOperationData({ ep1: serializeVector(point) }));
                    dispatch(setOperationStep(1));
                    dispatch(setCommandMessage("Eksen 2. ucunu seçin"));
                } else if (step === 1) {
                    dispatch(setOperationData({ ...data, ep2: serializeVector(point) }));
                    dispatch(setOperationStep(2));
                    dispatch(setCommandMessage("Kısa eksen mesafesini seçin"));
                } else if (step === 2) {
                    const ep1 = new THREE.Vector3(data.ep1.x, data.ep1.y, data.ep1.z);
                    const ep2 = new THREE.Vector3(data.ep2.x, data.ep2.y, data.ep2.z);
                    const center = ep1.clone().add(ep2).multiplyScalar(0.5);
                    const majorRadius = ep1.distanceTo(ep2) / 2;
                    const majorVec = ep2.clone().sub(ep1);
                    const angle = Math.atan2(majorVec.y, majorVec.x);
                    const minorRadius = point.clone().sub(center).length();

                    const ellipsePoints = createEllipsePoints(center, majorRadius, minorRadius, angle);
                    const geometry = new THREE.BufferGeometry().setFromPoints(ellipsePoints);
                    const material = new THREE.LineBasicMaterial({ color: 0xffffff });
                    const ellipse = new THREE.LineLoop(geometry, material);
                    ellipse.userData = {
                        id: generateUniqueId(),
                        isSelectable: true,
                        type: 'ellipse',
                        center: { x: center.x, y: center.y, z: center.z },
                        radiusX: majorRadius,
                        radiusY: minorRadius,
                        rotation: angle,
                        major: { x: ep2.x, y: ep2.y, z: ep2.z },
                    };
                    scene.add(ellipse);

                    // Redux'a DXF entity olarak kaydet (JBZ'ye dahil edilecek)
                    const majorEndPoint = ep2.clone().sub(center);
                    dispatch(addUserEntity({
                        type: 'ELLIPSE',
                        center: { x: center.x, y: center.y, z: center.z || 0 },
                        majorEndPoint: { x: majorEndPoint.x, y: majorEndPoint.y, z: 0 },
                        axisRatio: minorRadius / majorRadius,
                        startAngle: 0,
                        endAngle: Math.PI * 2,
                        layer: activeLayerName || '0',
                        _userEntityId: ellipse.userData.id,
                        _userDrawn: true,
                        _tabId: activeTabId || 'mimari',
                    }));

                    // Undo-redo kaydı
                    const manager = window.undoRedoManager;
                    if (manager) {
                        const tx = manager.beginTransaction('Draw Ellipse');
                        manager.addCommand(new DrawCommand(ellipse));
                        manager.commitTransaction(tx);
                    }

                    removeTempEllipse();
                    dispatch(resetOperation());
                    dispatch(setCommandMessage(""));
                }
            }
        };

        const handleClick = createDrawingClickHandler({
            scene,
            camera,
            renderer,
            snapPoints,
            snapMarkerRef,
            gridMode,
            objectSnap,
            snapMode,
            onPointSelected: handleEllipsePoint
        });

        const handleMouseMove = (e) => {
            const rect = domElement.getBoundingClientRect();
            const x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
            const y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
            const mouseNDC = new THREE.Vector2(x, y);
            const worldPoint = new THREE.Vector3(x, y, 0).unproject(camera);

            const { finalPoint, snapped, snapSource } = getSnappedPoint(
                mouseNDC,
                worldPoint,
                snapPoints,
                camera,
                renderer,
                { snapMode }
            );
            const point = (objectSnap && snapped) ? finalPoint.clone() : (gridMode ? applyGridSnap(worldPoint) : worldPoint.clone());

            // Snap marker
            if (snapMarkerRef.current) {
                scene.remove(snapMarkerRef.current);
                snapMarkerRef.current.geometry.dispose();
                snapMarkerRef.current.material.dispose();
                snapMarkerRef.current = null;
            }

            if (snapped && snapSource) {
                const marker = createSnapMarker(snapSource);
                marker.position.copy(finalPoint);
                scene.add(marker);
                snapMarkerRef.current = marker;
            }

            // Preview
            removeTempEllipse();

            if (method === 'center_axis') {
                if (step >= 1 && data.center) {
                    const center = new THREE.Vector3(data.center.x, data.center.y, data.center.z);
                    const major = (step === 1)
                        ? (orthoMode ? getOrthoSnappedPoint(center, point) : point)
                        : new THREE.Vector3(data.major.x, data.major.y, data.major.z);

                    const majorVec = major.clone().sub(center);
                    const majorRadius = majorVec.length();
                    const angle = Math.atan2(majorVec.y, majorVec.x);
                    const minorRadius = (step === 2) ? point.clone().sub(center).length() : majorRadius * 0.5;

                    const pts = createEllipsePoints(center, majorRadius, minorRadius, angle);
                    const geometry = new THREE.BufferGeometry().setFromPoints(pts);
                    const material = new THREE.LineBasicMaterial({ color: 0xffff00 });
                    tempEllipseRef.current = new THREE.Line(geometry, material);
                    scene.add(tempEllipseRef.current);
                }
            } else if (method === 'axis_endpoints') {
                if (step === 1 && data.ep1) {
                    // Show major axis line preview
                    const ep1 = new THREE.Vector3(data.ep1.x, data.ep1.y, data.ep1.z);
                    const lineGeom = new THREE.BufferGeometry().setFromPoints([ep1, point.clone()]);
                    const lineMat = new THREE.LineBasicMaterial({ color: 0xffff00 });
                    tempEllipseRef.current = new THREE.Line(lineGeom, lineMat);
                    scene.add(tempEllipseRef.current);
                } else if (step === 2 && data.ep1 && data.ep2) {
                    const ep1 = new THREE.Vector3(data.ep1.x, data.ep1.y, data.ep1.z);
                    const ep2 = new THREE.Vector3(data.ep2.x, data.ep2.y, data.ep2.z);
                    const center = ep1.clone().add(ep2).multiplyScalar(0.5);
                    const majorRadius = ep1.distanceTo(ep2) / 2;
                    const majorVec = ep2.clone().sub(ep1);
                    const angle = Math.atan2(majorVec.y, majorVec.x);
                    const minorRadius = point.clone().sub(center).length();

                    const pts = createEllipsePoints(center, majorRadius, minorRadius, angle);
                    const geometry = new THREE.BufferGeometry().setFromPoints(pts);
                    const material = new THREE.LineBasicMaterial({ color: 0xffff00 });
                    tempEllipseRef.current = new THREE.Line(geometry, material);
                    scene.add(tempEllipseRef.current);
                }
            }
        };

        const handleKeyDown = createEscapeHandler({
            scene,
            dispatch,
            tempObjectRef: tempEllipseRef,
            snapMarkerRef,
            additionalCleanup: () => {
                dispatch(setCommandMessage(""));
            }
        });

        domElement.addEventListener('click', handleClick);
        domElement.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('keydown', handleKeyDown);

        return () => {
            domElement.removeEventListener('click', handleClick);
            domElement.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [commandType, step, data, scene, camera, renderer, dispatch, gridMode, objectSnap, snapMode, orthoMode, drawMethod]);
};

export default useDrawEllipse;
